<template>
    <LoaderComponent v-if="enregistrementEnCours" />
    <div v-else class="modal-content">
        <h2>
            {{ $t('dashboard.parametrable.modale.widget.titre.' + step)
            }}<span v-if="step !== 1">" {{ widgetSelectionne.libelle }} "</span>
        </h2>
        <ReglePastille v-if="step === 1" cle="editionDashboard.widgetFacturationReserve" placement="bottom" />
        <CreationWidgetStepsBar
            :step="step"
            :without-step-one="widgetAModifier != null || widgetADupliquer != null"
        ></CreationWidgetStepsBar>

        <div class="bar-horizontale"></div>

        <div v-if="step === 1" class="container-widget">
            <div
                v-for="widget in listeWidgets"
                :key="widget.id"
                class="div-widget-container"
                @click="selectWidget(widget)"
            >
                <div class="width-5vw">
                    <img v-if="widget.id === 1" :src="loadImageDonut" alt="" class="image-widget" />
                    <img v-if="widget.id === 2" :src="loadImageBarresEtDonut" alt="" class="image-widget" />
                    <img v-if="widget.id >= 3" :src="loadImageBarres" alt="" class="image-widget-large" />
                </div>
                <div class="column margin-left-20px">
                    <h4 class="h4">{{ $t(widget.libelle) }}</h4>
                    <div class="description">{{ $t(widget.description) }}</div>
                </div>
                <div class="arrow">
                    <img :src="loadIconArrowright" alt="" class="image-arrow" />
                </div>
            </div>
        </div>

        <div v-else-if="listeSitesDisponibles.length !== 0 && step === 2" class="flex">
            <div class="width-50pct">
                <h4 class="margin-sites-dispo">
                    {{ $t('dashboard.parametrable.modale.creation.dashboard.bouton.sites.disponibles') }}
                </h4>
                <div class="input-container-with-icon">
                    <img :src="loadIconSearch" alt="search" class="icon-search" />
                    <input
                        v-model="filter"
                        :placeholder="
                            $t('dashboard.parametrable.modale.creation.dashboard.bouton.sites.recherche.placeholder')
                        "
                        class="input-recherche-site input"
                        @keyup="filtrerSite"
                    />
                </div>
                <div class="demi-bar-horizontale"></div>
                <check-box
                    :checked="
                        listeSitesSelectionnees == null ||
                        listeSitesDisponibles.length === listeSitesSelectionnees.length
                    "
                    :label="$t('dashboard.parametrable.modale.creation.dashboard.bouton.sites.tous')"
                    :partiel="
                        listeSitesSelectionnees != null &&
                        listeSitesSelectionnees?.length !== 0 &&
                        listeSitesSelectionnees?.length !== listeSitesDisponibles.length
                    "
                    @click="selectionnerTousLesSites"
                />
                <ReglePastille cle="editionDashboard.widgetPerimetreSites" />
                <ReglePastille cle="editionDashboard.widgetSelectionComportement" />
                <div class="demi-bar-horizontale-2"></div>
                <div class="sites-list-container">
                    <div v-for="site in listeSitesDisponibles" :key="site.id">
                        <check-box
                            :checked="isSiteSelectionnee(site.id)"
                            :label="site.description"
                            class="checkbox-site"
                            @click="ajouterSite(site)"
                        />
                    </div>
                </div>
            </div>
            <div class="bar-verticale width-50pct">
                <div class="margin-left-3pct">
                    <h4 class="margin-top-3pct">
                        {{ $t('dashboard.parametrable.modale.widget.titre.secondaire.perimetre') }}
                    </h4>
                </div>
                <div class="demi-bar-horizontale-right"></div>
                <div class="flex wrap sites-list-container recap-sites-list-container">
                    <div v-for="site in listeSitesSelectionnees" :key="site.id" class="pastille">
                        <span class="cursor-pointer" @click="ajouterSite(site)">{{ site.description }} X</span>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="step === 3">
            <h4>{{ $t('dashboard.parametrable.modale.widget.autre.parametre.titre') }}</h4>
            <div class="color-grey">{{ $t('dashboard.parametrable.modale.widget.autre.parametre.infos') }}</div>
            <input
                v-if="step === 3"
                v-model="denominationWidget"
                :placeholder="$t('dashboard.parametrable.modale.widget.autre.parametre.input.placeholder') + ' *'"
                class="input"
            />
            <ReglePastille cle="editionDashboard.widgetDenominationObligatoire" />
            <h4>{{ $t('dashboard.parametrable.modale.widget.autre.parametre.autre.parametres.titre') }}</h4>
        </div>

        <div v-if="step > 1" class="bar-horizontale"></div>
        <div v-if="step > 1" class="boutons">
            <div class="boutons-gauche">
                <ButtonWhite
                    :on-click="cancel"
                    :text="$t('dashboard.parametrable.modale.creation.dashboard.bouton.annuler')"
                    class="boutonAnnuler"
                />
                <ButtonWhite
                    v-if="step > 1 && (widgetADupliquer == null || widgetAModifier == null)"
                    :on-click="precedent"
                    :text="$t('dashboard.parametrable.modale.creation.dashboard.bouton.precedent')"
                    class="boutonPrecedent"
                />
            </div>
            <div class="boutons-droite">
                <BoutonBleu
                    :class="{ boutonCreer: step === 3, boutonEnregistrer: step === 2 }"
                    :disabled="isInputDisabled"
                    :on-click="suivant"
                    :text="textBoutonSuivant"
                    :with-check="step === 3"
                    class="boutonSuivant"
                />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import ButtonWhite from '@/components/ui-components/ButtonWhite.vue';
import BoutonBleu from '@/components/ui-components/BoutonBleu.vue';
import CheckBox from '@/components/ui-components/CheckBox.vue';
import { store } from '@/plugins/store';
import { Site } from '@/models/Site';
import { HomeAction } from '@/modules/Home/store/actions-types';
import { Widget } from '@/models/Widget';
import CreationWidgetStepsBar from '@/modules/Home/components/CreationWidgetStepsBar.vue';
import LoaderComponent from '@/components/ui-components/LoaderComponent.vue';
import ReglePastille from '@/components/regles/ReglePastille.vue';

interface Data {
    step: number;
    widgetId: number | null;
    listeSitesDisponibles: Site[];
    listeSitesSelectionnees: Site[];
    widgetSelectionne: Widget | null;
    denominationWidget: string;
    filter: string;
}

export default defineComponent({
    name: 'ModaleCreationWidget',
    components: { LoaderComponent, CreationWidgetStepsBar, CheckBox, BoutonBleu, ButtonWhite, ReglePastille },
    props: {
        widgetADupliquer: { type: Object as PropType<Widget>, required: false },
        widgetAModifier: { type: Object as PropType<Widget>, required: false },
    },
    data(): Data {
        return {
            step: this.widgetAModifier != undefined || this.widgetADupliquer != undefined ? 2 : 1,
            widgetId: null,
            listeSitesDisponibles: [],
            listeSitesSelectionnees: [],
            widgetSelectionne:
                this.widgetAModifier != undefined
                    ? this.widgetAModifier
                    : this.widgetADupliquer != undefined
                    ? this.widgetADupliquer
                    : null,
            denominationWidget: '',
            filter: '',
        };
    },
    async beforeMount() {
        await store.dispatch(HomeAction.GET_WIDGETS_DISPONIBLES);

        this.listeSitesDisponibles = store.getters.getSelectedDashboard.sites
            ? store.getters.getSelectedDashboard.sites
            : store.getters['siteList/getListValue'];
        if (this.widgetADupliquer) {
            this.widgetSelectionne = this.widgetADupliquer;
            this.listeSitesSelectionnees = this.$store.getters.getSelectedDashboard.sites;
            this.denominationWidget = this.widgetSelectionne.libelle;
        }
        if (this.widgetAModifier) {
            this.widgetSelectionne = this.widgetAModifier;
            this.listeSitesSelectionnees =
                this.widgetSelectionne.sites == null ? this.listeSitesDisponibles : this.widgetSelectionne.sites;
            this.denominationWidget = this.widgetSelectionne.libelle;
        }
    },
    computed: {
        enregistrementEnCours(): boolean {
            return this.$store.getters.getEnregistrementsDashboardEnCours;
        },
        listeWidgets() {
            return store.getters.getWidgets.filter((widget: Widget) => widget.id !== 5);
        },
        textBoutonSuivant(): string {
            if (this.widgetAModifier != null) {
                return this.step === 2
                    ? this.$t('dashboard.parametrable.modale.creation.dashboard.bouton.suivant')
                    : this.$t('dashboard.parametrable.modale.widget.bouton.modifier');
            }
            return this.step === 2
                ? this.$t('dashboard.parametrable.modale.creation.dashboard.bouton.suivant')
                : this.$t('dashboard.parametrable.modale.widget.bouton.ajouter');
        },
        isInputDisabled(): boolean {
            if (this.step === 3) {
                return this.denominationWidget.trim() === '';
            }
            if (this.step === 2) {
                return this.listeSitesSelectionnees != null && this.listeSitesSelectionnees.length === 0;
            }
            return false;
        },
        loadIconSearch() {
            return require('@/assets/img/icons/search.png');
        },
        loadImageDonut() {
            return require('@/assets/img/icons/imageDonut.png');
        },
        loadImageBarres() {
            return require('@/assets/img/icons/imageBarre.png');
        },
        loadImageBarresEtDonut() {
            return require('@/assets/img/icons/imageBarDonut.png');
        },
        loadIconArrowright() {
            return require('@/assets/img/arrow-right.svg');
        },
    },
    methods: {
        selectWidget(widget: Widget) {
            this.widgetSelectionne = widget;
            this.step = 2;
            this.denominationWidget = this.$t(widget.libelle);
        },
        isSiteSelectionnee(siteId: number): boolean {
            if (this.listeSitesSelectionnees == null) {
                return true;
            }
            return this.listeSitesSelectionnees.filter(s => s.id === siteId).length > 0;
        },
        selectionnerTousLesSites(): void {
            if (this.listeSitesSelectionnees == null || this.listeSitesSelectionnees.length !== 0) {
                this.listeSitesSelectionnees = [];
            } else {
                this.listeSitesSelectionnees = this.listeSitesDisponibles;
            }
        },
        ajouterSite(site: Site): void {
            if (this.listeSitesSelectionnees == null) {
                this.listeSitesSelectionnees = this.listeSitesDisponibles.filter(s => s.id !== site.id);
            } else if (this.listeSitesSelectionnees.filter(s => s.id === site.id).length > 0) {
                this.listeSitesSelectionnees = this.listeSitesSelectionnees.filter(s => s.id !== site.id);
            } else {
                this.listeSitesSelectionnees.push(site);
            }
        },
        cancel(): void {
            this.$emit('cancel');
        },
        precedent(): void {
            this.step--;
        },
        async suivant(): Promise<void> {
            if (this.step === 3 && this.widgetSelectionne != null) {
                let widget: Widget;
                if (this.widgetADupliquer == null && this.widgetAModifier == null) {
                    widget = new Widget(
                        store.getters.getSelectedDashboard.widgets == undefined
                            ? 0
                            : store.getters.getSelectedDashboard.widgets.length,
                        this.listeSitesSelectionnees,
                        this.denominationWidget,
                        store.getters.getSelectedDashboard.id,
                        this.widgetSelectionne.id
                    );
                } else {
                    widget = new Widget(
                        this.widgetADupliquer
                            ? store.getters.getSelectedDashboard.widgets.length
                            : this.widgetSelectionne.position,
                        this.listeSitesSelectionnees,
                        this.denominationWidget,
                        store.getters.getSelectedDashboard.id,
                        this.widgetSelectionne.typeWidgetId
                    );
                    widget.id = this.widgetADupliquer ? undefined : this.widgetSelectionne.id;
                }
                await store.dispatch(HomeAction.POST_WIDGET, widget);
                this.$emit('ajout-widget');
            } else {
                this.step++;
            }
        },
        filtrerSite(): void {
            this.listeSitesSelectionnees = [];
            this.listeSitesDisponibles = this.listeSitesDisponibles.filter(s =>
                s.description.toLowerCase().includes(this.filter.toLowerCase())
            );
        },
    },
});
</script>

<style scoped>
.modal-content {
    background: white;
    padding: 20px;
    border-radius: 10px;
    text-align: left;
    width: 50vw;
    max-height: 75vh;
}
.bar-horizontale {
    border-bottom: 1px solid #ccc;
    padding-bottom: 10px;
    margin-bottom: 10px;
    margin-left: -20px;
    margin-right: -20px;
}
.demi-bar-horizontale {
    border-bottom: 1px solid #ccc;
    padding-bottom: 10px;
    margin-bottom: 10px;
    margin-left: -20px;
}
.demi-bar-horizontale-2 {
    border-bottom: 1px solid #ccc;
    padding-bottom: 10px;
    margin-bottom: 10px;
    margin-right: 20px;
}
.demi-bar-horizontale-right {
    border-bottom: 1px solid #ccc;
    padding-bottom: 10px;
    margin-bottom: 10px;
    margin-right: -20px;
}
.bar-verticale {
    border-left: 1px solid #ccc;
    margin-top: -1%;
    margin-bottom: -1%;
}
:deep(.step-bar-container) {
    padding: 16px 0;
}
.input-container-with-icon {
    position: relative;
    width: 90%;
}
.icon-search {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    height: 20px;
    width: 20px;
    z-index: 1;
}
.input-recherche-site {
    width: 100% !important;
    padding-left: 35px !important;
    padding-right: 10px;
}
.input {
    width: 95%;
    padding: 10px 0 10px 10px;
    border: 1px solid var(--bleu-clair);
    border-radius: 5px;
    margin-top: 10px;
    margin-bottom: 10px;
}
.boutonAnnuler {
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    border-radius: 4px;
    padding: 10px;
    font-weight: 600;
    max-height: 50px;
    width: 120px;
    border: none;
}
.boutonPrecedent {
    display: flex;
    color: var(--bleu-clair);
    justify-content: center;
    align-items: center;
    cursor: pointer;
    border: 1px solid var(--bleu-clair);
    border-radius: 4px;
    padding: 10px;
    font-weight: 600;
    max-height: 50px;
    width: 120px;
}
.boutonSuivant {
    width: 120px;
    justify-content: center;
}
.boutonCreer {
    width: 170px;
}
.boutonEnregistrer {
    width: 120px;
}
.boutons {
    display: flex;
    justify-content: space-between;
}
.boutons-gauche {
    display: flex;
    gap: 10px;
}
.boutons-droite {
    display: flex;
    align-items: center;
    gap: 10px;
}
:deep(.step-number-container-valid) {
    background-color: var(--bleu-clair);
}
.checkbox-site {
    margin-bottom: 10px;
}
.width-50pct {
    width: 50%;
}
.margin-top-3pct {
    margin-top: 3%;
}
.margin-left-3pct {
    margin-left: 3%;
}
.pastille {
    margin: 5px;
    background-color: var(--background);
    border-radius: 20px;
    padding: 6px;
}
.wrap {
    flex-wrap: wrap;
}
.margin-sites-dispo {
    margin-top: 1%;
    margin-bottom: 2%;
}
.color-grey {
    color: grey;
}
.image-widget {
    width: 3vw;
}
.image-widget-large {
    width: 4vw;
    height: 3vh;
}
.div-widget-container {
    margin: 15px;
    border: 1px solid lightgrey;
    padding: 10px;
    border-radius: 6px;
    height: 5vh;
    cursor: pointer;
    display: flex;
    align-items: center;
}
.h4 {
    margin-bottom: 0;
    margin-top: 0;
}
.margin-left-20px {
    margin-left: 20px;
}
.description {
    color: grey;
    font-size: 14px;
}
.width-5vw {
    width: 5vw;
}
.arrow {
    margin-left: auto;
}
.image-arrow {
    width: 50%;
}
.container-widget {
    max-height: 50vh;
    overflow-y: auto;
}
.sites-list-container {
    max-height: 35vh;
    overflow-y: auto;
    margin-right: 20px;
    padding-right: 10px;
}
@media screen and (max-height: 850px) {
    .sites-list-container {
        max-height: 20vh;
    }
}
.recap-sites-list-container {
    max-height: 40vh;
}
@media screen and (max-height: 850px) {
    .recap-sites-list-container {
        max-height: 29vh;
    }
}
</style>
